import { Package, TrendingUp, Users, Wrench, DollarSign, Shield, Bell, BookOpen, AlertTriangle, Factory, FileBarChart, Trash2, LucideIcon } from 'lucide-react';

/** Feature codes that must be enabled for the article/category to appear. */
export type HelpFeatureCode =
  | 'INVENTORY'
  | 'ORDERS'
  | 'PRODUCTION'
  | 'PROCESSING'
  | 'WASTAGE'
  | 'REPORTS'
  | 'WORKFORCE'
  | 'ASSETS'
  | 'FINANCE'
  | 'APPROVALS'
  | 'NOTIFICATIONS';

export interface HelpArticle {
  id: string;
  title: string;
  slug: string;
  category: string;
  description: string;
  content: string; // plain text for search indexing
  relatedArticles: string[];
  /** If set, all listed features must be enabled (AND). Empty/undefined = always shown when category is. */
  requiredFeatures?: HelpFeatureCode[];
  /** If set, at least one listed feature must be enabled (OR). Combined with requiredFeatures when both present. */
  anyFeatures?: HelpFeatureCode[];
}

export interface HelpCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  description: string;
  requiredFeatures?: HelpFeatureCode[];
  anyFeatures?: HelpFeatureCode[];
}

export const helpCategories: HelpCategory[] = [
  { id: 'getting-started', label: 'Getting Started', icon: BookOpen, description: 'Learn MMS, set up your account, and explore the dashboard' },
  { id: 'inventory', label: 'Inventory', icon: Package, description: 'Manage raw materials and products', requiredFeatures: ['INVENTORY'] },
  { id: 'wastage', label: 'Wastage', icon: Trash2, description: 'Track and confirm material wastage', requiredFeatures: ['WASTAGE'] },
  { id: 'orders', label: 'Orders', icon: TrendingUp, description: 'Create and track customer orders', requiredFeatures: ['ORDERS'] },
  { id: 'factory-floor', label: 'Factory Floor', icon: Factory, description: 'Production batches and processing workflows', anyFeatures: ['PRODUCTION', 'PROCESSING'] },
  { id: 'reports', label: 'Reports', icon: FileBarChart, description: 'Operational and financial reports with export', requiredFeatures: ['REPORTS'] },
  { id: 'workforce', label: 'Workforce', icon: Users, description: 'Manage workers and assignments', requiredFeatures: ['WORKFORCE'] },
  { id: 'assets', label: 'Assets', icon: Wrench, description: 'Track assets and maintenance', requiredFeatures: ['ASSETS'] },
  { id: 'finance', label: 'Finance', icon: DollarSign, description: 'Bills, payments, and expenses', requiredFeatures: ['FINANCE'] },
  { id: 'approvals', label: 'Approvals', icon: Shield, description: 'Set up approval workflows', requiredFeatures: ['APPROVALS'] },
  { id: 'notifications', label: 'Notifications', icon: Bell, description: 'Configure alert preferences', requiredFeatures: ['NOTIFICATIONS'] },
  { id: 'troubleshooting', label: 'Troubleshooting', icon: AlertTriangle, description: 'Solve common issues' },
];

export const helpArticles: HelpArticle[] = [
  {
    id: 'welcome',
    title: 'Welcome to MMS',
    slug: 'welcome',
    category: 'getting-started',
    description: 'Introduction to MMS modules and where to start.',
    content: 'Welcome to MMS manufacturing management sidebar modules inventory orders factory finance workforce help center user journeys quick setup entities glossary.',
    relatedArticles: ['understanding-mms-entities', 'user-journeys-overview', 'quick-setup'],
  },
  {
    id: 'understanding-mms-entities',
    title: 'Understanding MMS Entities',
    slug: 'understanding-mms-entities',
    category: 'getting-started',
    description: 'Learn what orders, batches, parties, bills, and other records mean in MMS.',
    content: 'Understanding MMS entities raw materials products orders parties production batch processing wastage bills invoices workers shifts approvals notifications glossary.',
    relatedArticles: ['user-journeys-overview', 'tenant-features-modules', 'quick-setup'],
  },
  {
    id: 'user-journeys-overview',
    title: 'User Journeys Overview',
    slug: 'user-journeys-overview',
    category: 'getting-started',
    description: 'End-to-end journeys: setup, inbound, manufacturing, outbound, and finance.',
    content: 'User journeys overview setup inbound vendor manufacturing production processing outbound sales finance wastage approvals reports journey map what you do.',
    relatedArticles: ['tenant-features-modules', 'quick-setup', 'dashboard-overview'],
  },
  {
    id: 'tenant-features-modules',
    title: 'Your Modules and Plan',
    slug: 'tenant-features-modules',
    category: 'getting-started',
    description: 'Which sidebar modules appear for your organization and how Owners manage them.',
    content: 'Your modules and plan sidebar items inventory orders production processing workforce finance reports settings toggles owner manager staff role access.',
    relatedArticles: ['authentication-tenant-context', 'settings-tenant-admin', 'global-create-menu'],
  },
  {
    id: 'authentication-tenant-context',
    title: 'Sign In and Your Account',
    slug: 'authentication-tenant-context',
    category: 'getting-started',
    description: 'Log in, sign up, first-time onboarding, and manage your profile.',
    content: 'Sign in login signup account onboarding first time setup profile password organization tenant.',
    relatedArticles: ['tenant-features-modules', 'settings-tenant-admin'],
  },
  {
    id: 'global-create-menu',
    title: 'Global Create Menu',
    slug: 'global-create-menu',
    category: 'getting-started',
    description: 'What each Create menu item does and when it appears.',
    content: 'Global create menu product raw material order batch shift worker party processing template start batch when available.',
    relatedArticles: ['quick-setup', 'tenant-features-modules'],
  },
  {
    id: 'dashboard-overview',
    title: 'Dashboard Overview',
    slug: 'dashboard-overview',
    category: 'getting-started',
    description: 'Dashboard widgets, KPI cards, and click-through navigation.',
    content: 'Dashboard overview KPI widgets finance production orders inventory health refresh click navigate summary.',
    relatedArticles: ['user-journeys-overview', 'welcome'],
  },
  {
    id: 'activity-log-audit',
    title: 'Activity Log and History',
    slug: 'activity-log-audit',
    category: 'getting-started',
    description: 'View Activity on orders, batches, and bills to see what changed.',
    content: 'Activity log history view activity orders batches bills timeline status changes who when audit trail.',
    relatedArticles: ['track-orders', 'create-batch'],
  },
  {
    id: 'settings-tenant-admin',
    title: 'Settings & Tenant Admin',
    slug: 'settings-tenant-admin',
    category: 'getting-started',
    description: 'Owner and Manager settings: modules, users, GST, templates, and storefront.',
    content: 'Settings admin module features user management billing GST invoice templates marketplace owner manager toggles.',
    relatedArticles: ['tenant-features-modules', 'user-management-permissions', 'setup-rules'],
  },
  {
    id: 'user-management-permissions',
    title: 'User Management & Permissions',
    slug: 'user-management-permissions',
    category: 'getting-started',
    description: 'Manage team members, roles, hierarchy, and the complete MMS permission reference.',
    content: 'User management permissions roles hierarchy Owner Manager Staff inventory production processing orders finance workforce assets reports approvals notifications module features least privilege custom roles.',
    relatedArticles: ['settings-tenant-admin', 'tenant-features-modules', 'auth-permission-errors'],
  },
  {
    id: 'marketplace-storefront',
    title: 'Marketplace (Storefront)',
    slug: 'marketplace-storefront',
    category: 'getting-started',
    description: 'Online storefront orders and how staff fulfill them in MMS.',
    content: 'Marketplace storefront online store customer orders staff fulfillment settings integration.',
    relatedArticles: ['create-order', 'settings-tenant-admin'],
  },
  {
    id: 'quick-setup',
    title: 'Quick Setup Guide',
    slug: 'quick-setup',
    category: 'getting-started',
    description: 'Step-by-step path from empty account to your first order.',
    content: 'Quick setup guide raw materials products workers shifts production batch first order getting started wizard.',
    relatedArticles: ['welcome', 'add-raw-material', 'create-order', 'user-journeys-overview'],
  },
  {
    id: 'add-raw-material',
    title: 'Add Raw Materials',
    slug: 'add-raw-material',
    category: 'inventory',
    description: 'Learn how to add and manage raw materials in your inventory.',
    content: 'Add raw materials to your MMS inventory. Navigate to the inventory page, click Add Material, fill in the material name, unit, quantity, and cost, then save. Manage stock levels and track consumption.',
    relatedArticles: ['manage-products', 'quick-setup', 'wastage-tracking'],
    requiredFeatures: ['INVENTORY'],
  },
  {
    id: 'manage-products',
    title: 'Manage Products',
    slug: 'manage-products',
    category: 'inventory',
    description: 'Create and manage finished products and their compositions.',
    content: 'Manage products in MMS. Create new products by specifying name, category, unit price, and bill of materials. Define raw material compositions for each product to enable production tracking.',
    relatedArticles: ['add-raw-material', 'create-batch'],
    requiredFeatures: ['INVENTORY'],
  },
  {
    id: 'wastage-tracking',
    title: 'Wastage Tracking',
    slug: 'wastage-tracking',
    category: 'wastage',
    description: 'Report, confirm, and review material wastage.',
    content: 'Wastage tracking in MMS. Create draft wastage entries, confirm to adjust inventory or approve processing losses, filter by type and source, and review wastage reports under Stock and Inventory.',
    relatedArticles: ['add-raw-material', 'processing-runs', 'stock-inventory-reports'],
    requiredFeatures: ['WASTAGE'],
  },
  {
    id: 'create-order',
    title: 'Create an Order',
    slug: 'create-order',
    category: 'orders',
    description: 'Step-by-step guide to creating a new customer order.',
    content: 'Create a new order in MMS. Click the Create Order button, select the customer, add products with quantities, set delivery date, and submit. Track order status from open to delivered. Raw material lines require quantity and unit cost before confirm.',
    relatedArticles: ['track-orders', 'inbound-vendor-orders', 'create-batch'],
    requiredFeatures: ['ORDERS'],
  },
  {
    id: 'inbound-vendor-orders',
    title: 'Inbound Vendor Orders',
    slug: 'inbound-vendor-orders',
    category: 'orders',
    description: 'Vendor purchase orders, delivery, stock in, and purchase bills.',
    content: 'Inbound vendor orders purchase order receive stock delivered purchase bill finance vendor party.',
    relatedArticles: ['create-order', 'create-bill', 'track-orders'],
    requiredFeatures: ['ORDERS'],
  },
  {
    id: 'track-orders',
    title: 'Track Order Status',
    slug: 'track-orders',
    category: 'orders',
    description: 'Monitor order status, KPI cards, linked batches, and cancellations.',
    content: 'Track orders status open production ready shipped delivered delayed at-risk inventory-blocked filter cancel batches.',
    relatedArticles: ['create-order'],
    requiredFeatures: ['ORDERS'],
  },
  {
    id: 'factory-floor-overview',
    title: 'Factory Floor Overview',
    slug: 'factory-floor-overview',
    category: 'factory-floor',
    description: 'Navigate production and processing from the Factory Floor.',
    content: 'Factory Floor overview production processing KPI cards carousel tabs templates inventory batches dual layout with or without processing.',
    relatedArticles: ['create-batch', 'processing-runs', 'inventory-batch-management'],
    anyFeatures: ['PRODUCTION', 'PROCESSING'],
  },
  {
    id: 'create-batch',
    title: 'Create a Production Batch',
    slug: 'create-batch',
    category: 'factory-floor',
    description: 'Start a production batch, plan workers, and complete manufacturing.',
    content: 'Create production batch plan workers equipment start complete raw materials finished goods factory floor inventory batches.',
    relatedArticles: ['factory-floor-overview', 'create-order', 'manage-products', 'add-worker'],
    anyFeatures: ['PRODUCTION', 'PROCESSING'],
  },
  {
    id: 'inventory-batch-management',
    title: 'Inventory Batch Management',
    slug: 'inventory-batch-management',
    category: 'factory-floor',
    description: 'Track inbound, outbound, and production stock movements on Factory Floor.',
    content: 'Inventory batch management inbound outbound production pack fulfill filter status factory floor stock movements.',
    relatedArticles: ['factory-floor-overview', 'create-batch', 'track-orders'],
    anyFeatures: ['PRODUCTION', 'PROCESSING'],
  },
  {
    id: 'processing-runs',
    title: 'Processing Runs & Templates',
    slug: 'processing-runs',
    category: 'factory-floor',
    description: 'Run raw-material processing workflows on the Factory Floor.',
    content: 'Processing runs templates recipes start batch steps cancel wastage confirm inventory factory floor.',
    relatedArticles: ['factory-floor-overview', 'wastage-tracking', 'add-raw-material'],
    requiredFeatures: ['PROCESSING'],
  },
  {
    id: 'stock-inventory-reports',
    title: 'Stock & Inventory Reports',
    slug: 'stock-inventory-reports',
    category: 'reports',
    description: 'Stock summary, movements, low stock, and wastage reports.',
    content: 'Stock and Inventory reports in MMS. Generate stock summary, movements, low stock, and wastage reports with filters and Excel or PDF export.',
    relatedArticles: ['wastage-tracking', 'production-batch-report', 'order-summary-report'],
    requiredFeatures: ['REPORTS', 'INVENTORY'],
  },
  {
    id: 'order-summary-report',
    title: 'Order Summary Report',
    slug: 'order-summary-report',
    category: 'reports',
    description: 'Outbound order aggregates, register, and at-risk breakdown.',
    content: 'Order summary side panel filters multi-select OR union register at-risk payment due export excel pdf.',
    relatedArticles: ['track-orders', 'purchase-reports-guide'],
    requiredFeatures: ['REPORTS', 'ORDERS'],
  },
  {
    id: 'purchase-reports-guide',
    title: 'Purchase Reports',
    slug: 'purchase-reports-guide',
    category: 'reports',
    description: 'Inbound vendor PO register, payment due, and entity filters.',
    content: 'Purchase reports side panel multi-select filters OR union PO register payment due export excel pdf.',
    relatedArticles: ['inbound-vendor-orders', 'order-summary-report'],
    requiredFeatures: ['REPORTS', 'ORDERS'],
  },
  {
    id: 'production-batch-report',
    title: 'Production Batch Report',
    slug: 'production-batch-report',
    category: 'reports',
    description: 'Production batch planned vs actual quantities and loss tracking.',
    content: 'Production batch report planned actual loss quantities filters export excel pdf manufacturing.',
    relatedArticles: ['create-batch', 'stock-inventory-reports'],
    requiredFeatures: ['REPORTS', 'PRODUCTION'],
  },
  {
    id: 'revenue-report',
    title: 'Revenue Report',
    slug: 'revenue-report',
    category: 'reports',
    description: 'Sales invoices, payments, and revenue registers.',
    content: 'Revenue report sales invoices payments collection product breakdown filters export excel pdf.',
    relatedArticles: ['create-bill', 'expense-report'],
    requiredFeatures: ['REPORTS', 'FINANCE'],
  },
  {
    id: 'expense-report',
    title: 'Expense Report',
    slug: 'expense-report',
    category: 'reports',
    description: 'Purchase bills and expense category breakdown.',
    content: 'Expense report purchase bills expense category breakdown filters export excel pdf.',
    relatedArticles: ['create-bill', 'revenue-report'],
    requiredFeatures: ['REPORTS', 'FINANCE'],
  },
  {
    id: 'tax-gst-report',
    title: 'Tax & GST Report',
    slug: 'tax-gst-report',
    category: 'reports',
    description: 'Sales register, HSN summary, and purchase register.',
    content: 'Tax GST report sales register HSN summary purchase register filters export excel pdf.',
    relatedArticles: ['revenue-report', 'settings-tenant-admin'],
    requiredFeatures: ['REPORTS', 'FINANCE'],
  },
  {
    id: 'add-worker',
    title: 'Add a Worker',
    slug: 'add-worker',
    category: 'workforce',
    description: 'Register new workers and assign roles.',
    content: 'Add workers to MMS. Enter worker name, phone, role, daily wage, and specialization. Assign workers to production batches and track their attendance, productivity, and cost analysis.',
    relatedArticles: ['manage-assignments', 'create-batch'],
    requiredFeatures: ['WORKFORCE'],
  },
  {
    id: 'manage-assignments',
    title: 'Manage Worker Assignments',
    slug: 'manage-assignments',
    category: 'workforce',
    description: 'Assign workers to batches and track productivity.',
    content: 'Manage worker assignments in MMS. Assign workers to production batches, set shifts, track hours worked and output. View productivity analytics and cost breakdown per worker.',
    relatedArticles: ['add-worker', 'workforce-attendance', 'create-batch'],
    requiredFeatures: ['WORKFORCE'],
  },
  {
    id: 'workforce-attendance',
    title: 'Workforce Attendance',
    slug: 'workforce-attendance',
    category: 'workforce',
    description: 'Mark attendance and use present-only worker pickers on production.',
    content: 'Workforce attendance tab mark present absent production plan processing worker picker shift.',
    relatedArticles: ['add-worker', 'manage-assignments'],
    requiredFeatures: ['WORKFORCE'],
  },
  {
    id: 'register-asset',
    title: 'Register an Asset',
    slug: 'register-asset',
    category: 'assets',
    description: 'Add and track manufacturing assets and equipment.',
    content: 'Register assets in MMS. Add equipment name, type, purchase date, cost, location, and condition. Track asset utilization, depreciation, and assign maintenance schedules.',
    relatedArticles: ['schedule-maintenance'],
    requiredFeatures: ['ASSETS'],
  },
  {
    id: 'schedule-maintenance',
    title: 'Schedule Maintenance',
    slug: 'schedule-maintenance',
    category: 'assets',
    description: 'Set up preventive and corrective maintenance schedules.',
    content: 'Schedule maintenance for assets in MMS. Create maintenance tasks with type, priority, due date, and assigned worker. Track maintenance history, upcoming tasks, and overdue items.',
    relatedArticles: ['register-asset'],
    requiredFeatures: ['ASSETS'],
  },
  {
    id: 'create-bill',
    title: 'Create a Bill',
    slug: 'create-bill',
    category: 'finance',
    description: 'Generate purchase bills and sales invoices.',
    content: 'Create bills in MMS. Add purchase bills for raw materials or sales invoices for customers. Specify vendor, line items, amounts, tax, and payment terms. Track bill status from pending to paid.',
    relatedArticles: ['manage-payments'],
    requiredFeatures: ['FINANCE'],
  },
  {
    id: 'manage-payments',
    title: 'Manage Payments',
    slug: 'manage-payments',
    category: 'finance',
    description: 'Record and track payments for bills and invoices.',
    content: 'Manage payments in MMS. Record payments against purchase bills and sales invoices. Support multiple payment methods: cash, bank transfer, UPI, cheque. Track partial payments and outstanding balances.',
    relatedArticles: ['create-bill'],
    requiredFeatures: ['FINANCE'],
  },
  {
    id: 'setup-rules',
    title: 'Set Up Approval Rules',
    slug: 'setup-rules',
    category: 'approvals',
    description: 'Configure approval workflows for financial actions.',
    content: 'Set up approval rules in MMS. Create rules that require Owner approval for financial actions like marking bills as paid. Configure thresholds, entity types, and approval chains for your organization.',
    relatedArticles: ['create-bill'],
    requiredFeatures: ['APPROVALS'],
  },
  {
    id: 'manage-notifications',
    title: 'Manage Notifications',
    slug: 'manage-notifications',
    category: 'notifications',
    description: 'Configure and manage your notification preferences.',
    content: 'Manage notifications in MMS. View all notifications, filter by category and priority. Mark notifications as read, configure email alerts, and set up notification preferences for different event types.',
    relatedArticles: [],
    requiredFeatures: ['NOTIFICATIONS'],
  },
  {
    id: 'common-issues',
    title: 'Common Issues & Solutions',
    slug: 'common-issues',
    category: 'troubleshooting',
    description: 'Solutions for frequently encountered problems.',
    content: 'Common issues solutions login dashboard approvals orders production wastage finance troubleshooting help guides.',
    relatedArticles: ['api-errors-overview', 'welcome'],
  },
  {
    id: 'api-errors-overview',
    title: 'Understanding Error Messages',
    slug: 'api-errors-overview',
    category: 'troubleshooting',
    description: 'What MMS error messages mean and what to do next.',
    content: 'Understanding error messages fix entry permission approval stock try again contact support retry flowchart.',
    relatedArticles: ['auth-permission-errors', 'order-api-errors', 'common-issues'],
  },
  {
    id: 'auth-permission-errors',
    title: 'Login and Permission Problems',
    slug: 'auth-permission-errors',
    category: 'troubleshooting',
    description: 'Fix sign-in issues and access denied situations.',
    content: 'Login permission problems session expired page not visible staff role module missing sidebar access denied plan.',
    relatedArticles: ['api-errors-overview', 'setup-rules'],
  },
  {
    id: 'order-api-errors',
    title: 'Order Problems',
    slug: 'order-api-errors',
    category: 'troubleshooting',
    description: 'Resolve order status, stock, and cancellation issues.',
    content: 'Order problems status change inventory-blocked edit cancel confirm at-risk delayed stock production.',
    relatedArticles: ['track-orders', 'inventory-production-errors', 'api-errors-overview'],
    requiredFeatures: ['ORDERS'],
  },
  {
    id: 'inventory-production-errors',
    title: 'Stock and Production Problems',
    slug: 'inventory-production-errors',
    category: 'troubleshooting',
    description: 'Fix stock shortages, batch failures, and processing issues.',
    content: 'Stock production problems raw materials finished goods batch start complete processing cancel wastage factory floor layout.',
    relatedArticles: ['create-batch', 'add-raw-material', 'order-api-errors'],
    anyFeatures: ['INVENTORY', 'PRODUCTION', 'PROCESSING'],
  },
  {
    id: 'finance-api-errors',
    title: 'Finance Problems',
    slug: 'finance-api-errors',
    category: 'troubleshooting',
    description: 'Fix bill, invoice, and payment issues.',
    content: 'Finance problems bill pending approval mark paid payment not showing cancel purchase invoice confusion.',
    relatedArticles: ['create-bill', 'manage-payments', 'setup-rules'],
    requiredFeatures: ['FINANCE'],
  },
];

export function featuresMatch(
  required: HelpFeatureCode[] | undefined,
  anyOf: HelpFeatureCode[] | undefined,
  enabled: Record<string, boolean>,
): boolean {
  if (required?.length) {
    if (!required.every((f) => enabled[f])) return false;
  }
  if (anyOf?.length) {
    if (!anyOf.some((f) => enabled[f])) return false;
  }
  return true;
}

export function isCategoryEnabled(
  category: HelpCategory,
  enabledFeatures: Record<string, boolean>,
): boolean {
  return featuresMatch(category.requiredFeatures, category.anyFeatures, enabledFeatures);
}

export function isArticleEnabled(
  article: HelpArticle,
  enabledFeatures: Record<string, boolean>,
): boolean {
  const category = getCategoryById(article.category);
  if (category && !isCategoryEnabled(category, enabledFeatures)) return false;
  return featuresMatch(article.requiredFeatures, article.anyFeatures, enabledFeatures);
}

export function getArticlesByCategory(categoryId: string): HelpArticle[] {
  return helpArticles.filter(a => a.category === categoryId);
}

export function getArticleById(id: string): HelpArticle | undefined {
  return helpArticles.find(a => a.id === id);
}

export function getCategoryById(id: string): HelpCategory | undefined {
  return helpCategories.find(c => c.id === id);
}
